import { INode } from 'shared/types/tree'
import { LeafTypes, NodeType, QuestionType } from 'shared/types/ingestion'
import {
  childNodesOfType,
  getAttachmentLeavesByName,
  getQuestionType,
  leafOfType,
} from '../../../utility/TreeHelpers'
import { breadthFirstNodeCheck } from 'shared/utils/tree'
import {
  changePlaceholderExternalIds,
  deleteDuplicateLeafTypes,
  deletePlaceholderWithoutContent,
  fixPlaceholderListIssue,
  fixPlaceholderMissingTargets,
  deleteExtraOrAddMissingQuestionAnswerList,
  fixTargetsMissingExternalIds,
  setPointsValue,
} from './QuestionFixes'
import {
  childListsOfType,
  getItemListExternalIds,
  getOrderItemExternalIds,
  getPlaceholderAnswerIds,
  getPlaceholderLists,
  getPlaceholderTemplate,
  getPlaceholderTemplateIds,
  getQuestionAnswerLists,
  getQuestionItemLists,
  getQuestionTargetLists,
  getTargetListExternalIds,
} from './utility'

function isEmptyNode(node: INode) {
  return !node.tree_id || !node.parent_id || !node.type || !node.id
}

// TODO: add check for multiple question type leaves

export enum QuestionIssues {
  PlaceholderMissingTemplate,
  TemplateHasNoPlaceholders,
  ExtraPlaceholderList,
  MissingPlaceholderList,
  MissingTargetIds,
  ExtraItemIds,
  HasEmptyNodes,
  MissingItemList,
  ExtraItemLists,
  ItemsWithoutTargetList,
  MissingTargetList,
  ExtraTargetLists,
  TargetMissingExternalId,
  InvertedItemListStructure,
  EmptyTargetNodes,
  EmptyItemNodes,
  MissingOrderAnswerNode,
  ExtraOrderAnswersNode,
  MultiChoiceMissingAnswers,
  MultiChoiceDuplicateAnswers,
  ShortAnswerMissingAnswerList,
  ShortAnswerExtraAnswerLists,
  RequiresManualFixing,
  OrderAnswerWrongBodyType,
  OrderAnswerMissingExternalIds,
  OrderAnswerExtraExternalIds,
  PlaceholderExternalIdChange,
  PlaceholderHasNoContent,
  ShortAnswerMissingCorrectAnswer,
  HasDuplicateLeafTypes,
  HasQuestionPointValues,
  NoQuestionType,
  DrawingQuestionHasStagingImage,
}

export const questionIssueStrings: { [k: number]: string } = {
  [QuestionIssues.PlaceholderMissingTemplate]: 'Missing placeholder template',
  [QuestionIssues.ExtraPlaceholderList]: 'Has extra placeholder list',
  [QuestionIssues.MissingPlaceholderList]: 'Missing placeholder list',
  [QuestionIssues.MissingTargetIds]:
    'Template has target ids that are missing in placeholders list',
  [QuestionIssues.HasEmptyNodes]: 'Has empty nodes',
  [QuestionIssues.TemplateHasNoPlaceholders]: 'Template has no placeholders',
  [QuestionIssues.MissingItemList]: 'No item list',
  [QuestionIssues.ExtraItemLists]: 'Extra item lists',
  [QuestionIssues.ItemsWithoutTargetList]: 'Target items in a bad state',
}

// Create an external id map for all nodes in this list
// Confirm the dups have the same content and sort them out
export function getQuestionPlaceholderNodeUpdates(question: INode): {
  toKeep: INode[]
  toDelete: INode[]
  toChangeExternalId: INode[]
} {
  const templateIds = getPlaceholderTemplateIds(question) || []
  const [placeholderList] = getPlaceholderLists(question)
  if (!placeholderList)
    return { toKeep: [], toDelete: [], toChangeExternalId: [] }
  const toDelete: INode[] = []
  const externalIdMap =
    placeholderList.nodes?.reduce((acc: { [k: string]: INode[] }, node) => {
      if (!node.body) {
        toDelete.push(node)
        return acc
      }
      const externalId = node.body.ExternalID
      if (!acc[externalId]) {
        acc[externalId] = []
      }
      acc[externalId].push(node)
      return acc
    }, {}) || {}
  return Object.keys(externalIdMap).reduce(
    (
      {
        toKeep,
        toDelete,
        toChangeExternalId,
      }: { toKeep: INode[]; toDelete: INode[]; toChangeExternalId: INode[] },
      externalId,
    ) => {
      const [firstNode, ...rest] = externalIdMap[externalId]
      const lookingFor = firstNode.body?.answer?.join(',')
      if (rest.find((n) => n.body?.answer?.join(',') !== lookingFor)) {
        // In this case we need to update external ids so they are not duplicated
        // but only if the external id is not in the template
        if (templateIds.includes(externalId)) {
          throw new Error(
            'dupd external id contains different answers and is included in template, not sure how to handle',
          )
        }
        toChangeExternalId.push(...rest)
      } else {
        toKeep.push(firstNode)
        toDelete.push(...rest)
      }
      return { toKeep, toDelete, toChangeExternalId }
    },
    { toKeep: [], toDelete, toChangeExternalId: [] },
  )
}

export interface IQuestionIssueItem {
  issue: QuestionIssues
  description?: string
  evaluator: (
    question: INode,
    curriculaData: { attributionCode: string; curriculaTitle: string },
  ) => boolean
  affectedTypes: QuestionType[]
  affectsAllTypes?: boolean
  userImpacting?: boolean
  requiresId?: boolean
  fix?: (question: INode) => Promise<unknown>
}

const issueGroups = {
  placeholderGroup: [QuestionType.Placeholder, QuestionType.PlaceholderDnD],
  itemAndTargetListGroup: [QuestionType.Matching, QuestionType.Classifying],
}

const allQuestionIssues: IQuestionIssueItem[] = [
  {
    issue: QuestionIssues.HasQuestionPointValues,
    evaluator: (question, curriculaData) => {
      if (curriculaData.curriculaTitle.toLowerCase().includes('assessment')) {
        return false
      }
      const pointsLeaf = leafOfType(question, LeafTypes.Points)
      if (!pointsLeaf) return true
      const pointsValue = pointsLeaf.body.value
      return pointsValue !== 4
    },
    affectsAllTypes: true,
    affectedTypes: [],
    fix: setPointsValue,
  },
  {
    issue: QuestionIssues.NoQuestionType,
    evaluator: (question) => {
      return !getQuestionType(question)
    },
    affectsAllTypes: true,
    requiresId: true,
    affectedTypes: [],
  },
  {
    issue: QuestionIssues.HasDuplicateLeafTypes,
    evaluator: (question: INode) => {
      const leafTypes: LeafTypes[] =
        question.leaves?.map(({ type }) => type as LeafTypes) || []
      const notDeleting = [LeafTypes.AppletGeogebra]
      const dups = leafTypes.filter(
        (t, i) => leafTypes.indexOf(t) !== i && !notDeleting.includes(t),
      )
      return !!dups.length
    },
    affectsAllTypes: true,
    affectedTypes: [],
    fix: deleteDuplicateLeafTypes,
  },
  {
    issue: QuestionIssues.HasEmptyNodes,
    evaluator: (question: INode) => {
      let hasEmptyNodes = false
      breadthFirstNodeCheck(question, (node) => {
        hasEmptyNodes = hasEmptyNodes || isEmptyNode(node)
      })
      return hasEmptyNodes
    },
    affectedTypes: [],
  },
  {
    issue: QuestionIssues.PlaceholderMissingTemplate,
    description: "without a template there's nothing for the student to do",
    evaluator: (question: INode) => {
      const [list] = getPlaceholderLists(question)
      return list && !getPlaceholderTemplate(question)
    },
    affectedTypes: issueGroups.placeholderGroup,
    userImpacting: true,
  },
  {
    issue: QuestionIssues.PlaceholderHasNoContent,
    description: 'this should be deleted',
    evaluator: (question: INode) => {
      const [list] = getPlaceholderLists(question)
      return !list?.nodes && !getPlaceholderTemplate(question)
    },
    affectedTypes: issueGroups.placeholderGroup,
    userImpacting: true,
    fix: deletePlaceholderWithoutContent,
  },
  {
    issue: QuestionIssues.TemplateHasNoPlaceholders,
    description:
      "Template has no placeholders, without placeholders there's nothing for the student to do",
    evaluator: (question: INode) => {
      const externalIds = getPlaceholderTemplateIds(question)
      if (!externalIds) return false
      return !externalIds.length
    },
    requiresId: true,
    affectedTypes: issueGroups.placeholderGroup,
    userImpacting: true,
  },
  {
    issue: QuestionIssues.MissingPlaceholderList,
    description: 'makes it unusable for student and un-editable for teachers',
    evaluator: (question: INode) => {
      return (
        !!getPlaceholderTemplateIds(question) &&
        getPlaceholderLists(question).length === 0
      )
    },
    affectedTypes: issueGroups.placeholderGroup,
  },
  {
    issue: QuestionIssues.ExtraPlaceholderList,
    evaluator: (question: INode) => {
      return getPlaceholderLists(question).length > 1
    },
    affectedTypes: issueGroups.placeholderGroup,
    fix: fixPlaceholderListIssue,
  },
  {
    issue: QuestionIssues.PlaceholderExternalIdChange,
    evaluator: (question: INode) => {
      const { toChangeExternalId } = getQuestionPlaceholderNodeUpdates(question)
      return !!toChangeExternalId.length
    },
    affectedTypes: issueGroups.placeholderGroup,
    fix: changePlaceholderExternalIds,
  },
  {
    issue: QuestionIssues.MissingTargetIds,
    description: 'there should be a 1:1 mapping of template ids to answer ids',
    evaluator: (question: INode) => {
      const templateIds = getPlaceholderTemplateIds(question)
      const answerIds = getPlaceholderAnswerIds(question)
      if (!answerIds || !templateIds) return false
      return !!templateIds.find((id) => !answerIds.includes(id))
    },
    affectedTypes: issueGroups.placeholderGroup,
    fix: fixPlaceholderMissingTargets,
  },
  {
    issue: QuestionIssues.MissingItemList,
    evaluator: (question: INode) => {
      return getQuestionItemLists(question).length === 0
    },
    affectedTypes: [
      ...issueGroups.itemAndTargetListGroup,
      QuestionType.Ordering,
    ],
  },
  {
    issue: QuestionIssues.ExtraItemLists,
    evaluator: (question: INode) => {
      return getQuestionItemLists(question).length > 1
    },
    affectedTypes: [
      ...issueGroups.itemAndTargetListGroup,
      QuestionType.Ordering,
    ],
  },
  {
    issue: QuestionIssues.InvertedItemListStructure,
    evaluator: (question: INode) => {
      const [itemList] = getQuestionItemLists(question)
      const badChild = itemList?.nodes.find((child) => !child.body?.targets)
      if (!badChild) return false
      return !!badChild.body?.ExternalID
    },
    affectedTypes: issueGroups.itemAndTargetListGroup,
  },
  {
    issue: QuestionIssues.ItemsWithoutTargetList,
    evaluator: (question: INode) => {
      const [itemList] = getQuestionItemLists(question)
      const badChild = itemList?.nodes.find((child) => !child.body?.targets)
      if (!badChild) return false
      // This rule is not flagging as expected, disabling for now
      return !badChild.body?.ExternalID && false
    },
    affectedTypes: issueGroups.itemAndTargetListGroup,
  },
  {
    issue: QuestionIssues.MissingTargetList,
    evaluator: (question: INode) => {
      return getQuestionTargetLists(question).length === 0
    },
    affectedTypes: issueGroups.itemAndTargetListGroup,
  },
  {
    issue: QuestionIssues.ExtraTargetLists,
    evaluator: (question: INode) => {
      return getQuestionTargetLists(question).length > 1
    },
    affectedTypes: issueGroups.itemAndTargetListGroup,
  },
  {
    issue: QuestionIssues.TargetMissingExternalId,
    evaluator: (question: INode) => {
      const [targetList] = getQuestionTargetLists(question)
      const targets = getTargetListExternalIds(targetList)
      return targets.length !== targetList.nodes.length
    },
    userImpacting: true,
    affectedTypes: issueGroups.itemAndTargetListGroup,
    fix: fixTargetsMissingExternalIds,
  },
  {
    issue: QuestionIssues.RequiresManualFixing,
    evaluator: (question: INode) => {
      const isEmptyTarget = (target: INode) => {
        return !target.description && !leafOfType(target, LeafTypes.Attachment)
      }
      const [targetList] = getQuestionTargetLists(question)
      const targets = getTargetListExternalIds(targetList)
      // This issue needs to be resolved before we can manually fix it
      if (targets.length !== targetList.nodes.length) return false
      return !!targetList.nodes.find(isEmptyTarget)
    },
    userImpacting: true,
    affectedTypes: issueGroups.itemAndTargetListGroup,
  },
  {
    issue: QuestionIssues.ExtraItemIds,
    evaluator: (question: INode) => {
      const [itemList] = getQuestionItemLists(question)
      if (!itemList) return false
      const [targetList] = getQuestionTargetLists(question)
      if (!targetList) return false
      const itemExternalIds = getItemListExternalIds(itemList)
      const targetExternalIds = getTargetListExternalIds(targetList)
      return !!itemExternalIds.find((id) => !targetExternalIds.includes(id))
    },
    affectedTypes: issueGroups.itemAndTargetListGroup,
  },
  {
    issue: QuestionIssues.MissingOrderAnswerNode,
    evaluator: (question: INode) => {
      return childNodesOfType(question, NodeType.QuestionAnswer).length === 0
    },
    affectedTypes: [QuestionType.Ordering],
  },
  {
    issue: QuestionIssues.ExtraOrderAnswersNode,
    evaluator: (question: INode) => {
      return childNodesOfType(question, NodeType.QuestionAnswer).length > 1
    },
    affectedTypes: [QuestionType.Ordering],
  },
  {
    issue: QuestionIssues.OrderAnswerWrongBodyType,
    evaluator: (question: INode) => {
      const [answeNode] = childNodesOfType(question, NodeType.QuestionAnswer)
      if (!answeNode) return false
      return answeNode.body?.type !== 'question:ordering:answer'
    },
    affectedTypes: [QuestionType.Ordering],
  },
  {
    issue: QuestionIssues.OrderAnswerMissingExternalIds,
    evaluator: (question: INode) => {
      const [answeNode] = childNodesOfType(question, NodeType.QuestionAnswer)
      if (!answeNode) return false
      const value = answeNode.body?.value || []
      const itemIds = getOrderItemExternalIds(question)
      return !!itemIds.find((id) => !value.includes(id))
    },
    affectedTypes: [QuestionType.Ordering],
  },
  {
    issue: QuestionIssues.OrderAnswerExtraExternalIds,
    evaluator: (question: INode) => {
      const [answeNode] = childNodesOfType(question, NodeType.QuestionAnswer)
      if (!answeNode) return false
      const value = answeNode.body?.value || []
      const itemIds = getOrderItemExternalIds(question)
      return !!value.find((id: string) => !itemIds.includes(id))
    },
    affectedTypes: [QuestionType.Ordering],
  },
  {
    issue: QuestionIssues.MultiChoiceMissingAnswers,
    evaluator: (question: INode) => {
      return getQuestionAnswerLists(question).length === 0
    },
    affectedTypes: [QuestionType.MultipleChoice, QuestionType.MultipleSelect],
  },
  {
    issue: QuestionIssues.MultiChoiceDuplicateAnswers,
    evaluator: (question: INode) => {
      return getQuestionAnswerLists(question).length > 1
    },
    affectedTypes: [QuestionType.MultipleChoice, QuestionType.MultipleSelect],
    fix: deleteExtraOrAddMissingQuestionAnswerList,
  },
  {
    issue: QuestionIssues.ShortAnswerMissingAnswerList,
    evaluator: (question: INode) => {
      return getQuestionAnswerLists(question).length === 0
    },
    affectedTypes: [QuestionType.ShortAnswer],
    fix: deleteExtraOrAddMissingQuestionAnswerList,
  },
  {
    issue: QuestionIssues.ShortAnswerExtraAnswerLists,
    evaluator: (question: INode) => {
      return getQuestionAnswerLists(question).length > 1
    },
    affectedTypes: [QuestionType.ShortAnswer],
    fix: deleteExtraOrAddMissingQuestionAnswerList,
  },
  {
    issue: QuestionIssues.ShortAnswerMissingCorrectAnswer,
    description:
      'We mightve lost the answer to this question, theres a teachers note, it might be there',
    evaluator: (question: INode) => {
      // IDs are no longer finding value with this issue being reported
      return false
      // eslint-disable-next-line no-unreachable
      const [teacherNote] = childListsOfType(
        question,
        NodeType.Inline,
        NodeType.Note,
      )
      if (!teacherNote || !teacherNote.nodes) return false
      const [answerList] = getQuestionAnswerLists(question)
      if (!answerList) return false
      const [answerNode] = answerList.nodes
      return !answerNode || !answerNode.body?.value
    },
    requiresId: true,
    affectedTypes: [QuestionType.ShortAnswer],
  },
  {
    issue: QuestionIssues.DrawingQuestionHasStagingImage,
    description: 'Drawing question background images cant come from staging',
    evaluator: (question: INode) => {
      const attachments = getAttachmentLeavesByName(question)
      return !!attachments.find(
        (a) => !!a.body?.file?.url?.includes('kiddom-media-staging'),
      )
    },
    requiresId: false,
    affectedTypes: [QuestionType.Drawing],
  },
]

export interface IQuestionIssueResolution {
  questionId: string
  issues: QuestionIssues[]
  userImpacting?: boolean
  requiresId?: boolean
  descriptions: string[]
  fixes: ((question: INode) => Promise<unknown>)[]
}

export function getQuestionIssues(
  question: INode,
  curriculaData: {
    attributionCode: string
    curriculaTitle: string
  },
): IQuestionIssueResolution {
  const questionType = getQuestionType(question)
  const potentialIssues = allQuestionIssues.filter(
    (qi) => qi.affectsAllTypes || qi.affectedTypes.includes(questionType),
  )
  return potentialIssues.reduce(
    (acc: IQuestionIssueResolution, issue) => {
      if (issue.evaluator(question, curriculaData)) {
        acc.issues.push(issue.issue)
        if (issue.fix && !acc.fixes.includes(issue.fix)) {
          acc.fixes.push(issue.fix)
        }
        acc.userImpacting = acc.userImpacting || issue.userImpacting
        acc.requiresId = acc.requiresId || issue.requiresId
        issue.description && acc.descriptions.push(issue.description)
      }
      return acc
    },
    { questionId: question.id, issues: [], fixes: [], descriptions: [] },
  )
}
